import React from "react";
import axios from "axios";
import {
  CONTACT_ADDRESS,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  CONTACT_PHONE_RAW,
  waLink,
  MAP_URL,
  MAP_COORDS,
  PLUS_CODE,
} from "@/lib/brand";
import Mnemonic from "@/components/Mnemonic";
import SaveNumberLink from "@/components/SaveNumberLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTY_OPTIONS = ["1 guest", "2 guests", "3–4 guests", "5–6 guests", "A larger group"];

export default function Contact() {
  const [form, setForm] = React.useState({
    name: "",
    contact: "",
    dates: "",
    party_size: "",
    message: "",
  });
  const [status, setStatus] = React.useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim() || !form.message.trim()) {
      setStatus({ state: "error", msg: "Please share your name, a way to reach you, and a note." });
      return;
    }
    try {
      setStatus({ state: "loading", msg: "" });
      await axios.post(`${API}/enquiry`, form);
      setStatus({
        state: "success",
        msg: "Your note has arrived at the pantry. We will read it and write back.",
      });
      setForm({ name: "", contact: "", dates: "", party_size: "", message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        msg: "Something did not travel. Please try again, or message us on WhatsApp.",
      });
    }
  };

  return (
    <div data-testid="page-contact">
      <section style={{ background: "var(--salt-white)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-14">
          <Mnemonic size={44} className="mb-8" style={{ opacity: 0.9 }} />
          <div
            className="italic-display mb-3"
            style={{ fontSize: "clamp(18px, 2vw, 24px)", color: "var(--sundari-gold)", opacity: 0.9 }}
            data-testid="page-bengali"
          >
            যোগাযোগ
          </div>
          <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>Contact</div>
          <h1 className="italic-display mt-6" style={{ fontSize: "clamp(48px, 7.5vw, 104px)", lineHeight: 1 }}>
            Write to us,
            <br />
            <span style={{ color: "var(--sundari-gold)" }}>slowly.</span>
          </h1>
          <p className="mt-8 max-w-[680px]" style={{ fontSize: "17px", lineHeight: 1.75 }}>
            Tell us when you might come, how many of you, and what you are hoping to see. We
            answer by hand — from the pantry, between the morning walk and lunch.
          </p>
        </div>
      </section>

      <section style={{ background: "var(--bamboo-cream)" }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10">
          {/* Primary: WhatsApp */}
          <aside className="md:col-span-5">
            <div className="hair-all rounded-[8px] p-8 md:p-10" style={{ background: "var(--mangrove-night)", color: "var(--bamboo-cream)" }}>
              <div className="eyebrow" style={{ color: "var(--sundari-gold)" }}>The Fastest Path</div>
              <h2 className="italic-display mt-4" style={{ fontSize: "34px", lineHeight: 1.1 }}>
                Message us on WhatsApp.
              </h2>
              <p className="mt-4" style={{ fontSize: "14.5px", lineHeight: 1.75, opacity: 0.82 }}>
                Someone at the pantry reads it between 07:00 and 21:00 IST.
              </p>
              <a
                href={waLink("Hello FarmStory, I'd like to plan a visit.")}
                target="_blank"
                rel="noreferrer"
                className="btn-pill btn-pill--solid mt-8"
                data-testid="contact-cta-whatsapp"
              >
                Message on WhatsApp
              </a>
              <div className="mt-5">
                <SaveNumberLink tone="light" testId="contact-save-number" />
              </div>

              <div className="hair-t-light mt-10 pt-6 space-y-4 text-[13.5px]" style={{ opacity: 0.86 }}>
                <div>
                  <div className="label-track" style={{ color: "var(--sundari-gold)" }}>Where</div>
                  <div className="mt-1" data-testid="contact-address">{CONTACT_ADDRESS}</div>
                  <div className="mt-1 label-track" style={{ opacity: 0.62, fontSize: "10.5px" }}>
                    Plus Code · {PLUS_CODE}
                  </div>
                  <a
                    href={MAP_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="u-link link-fade mt-2 inline-block"
                    style={{ color: "var(--sundari-gold)" }}
                    data-testid="contact-map-link"
                  >
                    Open in Google Maps →
                  </a>
                </div>
                <div>
                  <div className="label-track" style={{ color: "var(--sundari-gold)" }}>Email</div>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="u-link link-fade mt-1 inline-block" data-testid="contact-email">
                    {CONTACT_EMAIL}
                  </a>
                </div>
                <div>
                  <div className="label-track" style={{ color: "var(--sundari-gold)" }}>Voice</div>
                  <a href={`tel:+${CONTACT_PHONE_RAW}`} className="u-link link-fade mt-1 inline-block" data-testid="contact-phone">
                    {CONTACT_PHONE}
                  </a>
                </div>
              </div>
            </div>
          </aside>

          {/* Enquiry form */}
          <form
            onSubmit={submit}
            className="md:col-span-7"
            data-testid="enquiry-form"
            noValidate
          >
            <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>Or, an Enquiry</div>
            <h3 className="italic-display mt-4" style={{ fontSize: "clamp(30px, 3.6vw, 44px)", lineHeight: 1.05 }}>
              Leave a note for the pantry.
            </h3>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              <div className="col-span-1 sm:col-span-2">
                <label className="label-track" style={{ color: "var(--delta-clay)" }}>Your name</label>
                <input
                  className="field"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="e.g., Anindita Roy"
                  data-testid="enquiry-name"
                  required
                />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <label className="label-track" style={{ color: "var(--delta-clay)" }}>Email or phone</label>
                <input
                  className="field"
                  value={form.contact}
                  onChange={update("contact")}
                  placeholder="how we should write back"
                  data-testid="enquiry-contact"
                  required
                />
              </div>
              <div>
                <label className="label-track" style={{ color: "var(--delta-clay)" }}>Approximate dates</label>
                <input
                  className="field"
                  value={form.dates}
                  onChange={update("dates")}
                  placeholder="e.g., late Nov, 3 nights"
                  data-testid="enquiry-dates"
                />
              </div>
              <div>
                <label className="label-track" style={{ color: "var(--delta-clay)" }}>Party size</label>
                <select
                  className="field"
                  value={form.party_size}
                  onChange={update("party_size")}
                  data-testid="enquiry-party"
                >
                  <option value="">Select</option>
                  {PARTY_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-1 sm:col-span-2">
                <label className="label-track" style={{ color: "var(--delta-clay)" }}>Your note</label>
                <textarea
                  className="field"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="What you would like to see, taste, or ask."
                  rows={5}
                  data-testid="enquiry-message"
                  required
                />
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <button
                type="submit"
                className="btn-pill btn-pill--dark"
                disabled={status.state === "loading"}
                data-testid="enquiry-submit"
              >
                {status.state === "loading" ? "Sending" : "Send Enquiry"}
              </button>
              {status.state === "success" && (
                <p
                  className="text-[13.5px]"
                  style={{ color: "var(--golpata-green)", fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "16px" }}
                  data-testid="enquiry-success"
                >
                  {status.msg}
                </p>
              )}
              {status.state === "error" && (
                <p
                  className="text-[13.5px]"
                  style={{ color: "var(--dusk-coral)" }}
                  data-testid="enquiry-error"
                >
                  {status.msg}
                </p>
              )}
            </div>

            <p className="mt-10 label-track" style={{ color: "var(--delta-clay)", opacity: 0.72 }}>
              This is an enquiry — not a booking. We reply within a day.
            </p>
          </form>
        </div>
      </section>

      {/* Finding Us — Map */}
      <section
        style={{ background: "var(--mangrove-night)", color: "var(--bamboo-cream)" }}
        data-testid="finding-us"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
            <div className="md:col-span-5">
              <div className="eyebrow" style={{ color: "var(--sundari-gold)" }}>Finding Us</div>
              <h2 className="italic-display mt-4" style={{ fontSize: "clamp(32px, 4vw, 46px)", lineHeight: 1.05 }}>
                On the road to Jharkhali.
              </h2>
              <hr className="divider-gold mt-6" />
              <p className="mt-6" style={{ fontSize: "15px", lineHeight: 1.75, opacity: 0.85 }}>
                Near Raja More, Jharkhali. From Kolkata, about three hours by road; the last stretch
                is quieter. If you tell us when you are starting, we will send you the way we take.
              </p>
              <div className="mt-6 text-[13px] space-y-1" style={{ opacity: 0.78 }}>
                <div>{CONTACT_ADDRESS}</div>
                <div className="label-track" style={{ opacity: 0.7, fontSize: "10.5px" }}>
                  Plus Code · {PLUS_CODE}
                </div>
                <div className="label-track" style={{ opacity: 0.7, fontSize: "10.5px" }}>
                  {MAP_COORDS.lat.toFixed(4)}° N · {MAP_COORDS.lng.toFixed(4)}° E
                </div>
              </div>
              <a
                href={MAP_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-pill btn-pill--solid mt-8"
                data-testid="map-cta"
              >
                Open in Google Maps
              </a>
            </div>
            <div className="md:col-span-7 hair-all rounded-[8px] overflow-hidden" style={{ borderColor: "rgba(250,247,240,0.14)" }}>
              <iframe
                title="FarmStory location"
                data-testid="map-embed"
                src={`https://www.google.com/maps?q=${MAP_COORDS.lat},${MAP_COORDS.lng}&hl=en&z=13&output=embed`}
                style={{ width: "100%", height: 420, border: 0, filter: "grayscale(0.35) contrast(0.95)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
